import { BrandMark } from "./BrandMark";
import { NEUTRAL_KICKER } from "../lib/hostConfig";
import { listScenarios, type ScenarioCard } from "../scenario/registry";

/**
 * Course library shown at the root URL (no ?scenario / ?spec). Lists every
 * built-in course as a clickable card so operators don't need to memorise links.
 * Each card links to ?scenario=<id>, which the app then loads normally.
 */

const ACCENTS: Record<string, { from: string; to: string; tag: string; emoji: string }> = {
  "phishing-office": { from: "#1d6fb8", to: "#0d3d63", tag: "Phishing", emoji: "\u{1F3E2}" },
  "deepfake-whaling": { from: "#2ba6c4", to: "#0d5566", tag: "Deepfake \u00B7 Whaling", emoji: "\u{1F3D6}\uFE0F" },
  "social-engineering": { from: "#b8702b", to: "#5a3410", tag: "Social Engineering", emoji: "\u{1F6AA}" },
  "quishing-restaurant": { from: "#c2473a", to: "#5a1f18", tag: "Quishing \u00B7 Deepfake", emoji: "\u{1F37D}\uFE0F" },
  "ransomware-office": { from: "#6b2b8a", to: "#2a1038", tag: "Ransomware", emoji: "\u{1F4BC}" },
  "spear-phishing-osint": { from: "#8a2b3a", to: "#38101a", tag: "Spear Phishing \u00B7 OSINT", emoji: "\u{1F3AF}" },
  "remote-work-max": { from: "#4aa89c", to: "#1a4a44", tag: "Remote Work \u00B7 BYOD", emoji: "\u{1F4F1}" },
  "office-walkable": { from: "#2d6a4f", to: "#143528", tag: "Photoreal walk \u00B7 WASD", emoji: "\u{1F6B6}" },
  "office-under-attack": { from: "#a83232", to: "#3a1414", tag: "Full Course \u00B7 Continuous Story", emoji: "\u{1F3E2}" },
  "bar-walkable": { from: "#c9527a", to: "#4a1230", tag: "Walkable \u00B7 Wi-Fi & Social Media", emoji: "\u{1F378}" },
  "holiday-call": { from: "#1a7a8c", to: "#0d3a44", tag: "Deepfake \u00B7 Payment Fraud", emoji: "\u{1F3D6}\uFE0F" },
};

const FALLBACK = { from: "#4aa89c", to: "#1f5a52", tag: "Training", emoji: "\u{1F3AF}" };

/**
 * Courses that are NOT built-ins in the REGISTRY (registry.ts) — authored via
 * a hosted ScenarioSpec JSON and loaded with `?spec=<url>` instead of
 * `?scenario=<id>`. `bar-walkable` is spec-only (published from
 * /var/lib/sim/assets/bar-walkable/, served at /cdn/sim/bar-walkable/), so it
 * has no REGISTRY entry and `listScenarios()` never returns it — list it here
 * instead so it still shows up as a normal clickable card on the Library
 * landing page. Each entry's `href` is used verbatim (bypasses the
 * `?scenario=` link-building in CourseCard).
 */
interface ExtraCard extends ScenarioCard {
  href: string;
}
const EXTRA_CARDS: ExtraCard[] = [
  {
    id: "bar-walkable",
    title: "Walkable Bar",
    topic:
      "WASD walk a real purchased 3D bar in first person \u2014 public Wi-Fi man-in-the-middle, a QR-code phishing sticker, and a social-media overshare that fuels a spear-phishing email, in one linked story.",
    language: "en",
    href: "?spec=/cdn/sim/bar-walkable/bar-walkable-spec.json",
  },
];

export function Library() {
  const courses: (ScenarioCard & { href?: string })[] = [...listScenarios(), ...EXTRA_CARDS];
  return (
    <div className="library">
      <header className="lib-head">
        <div className="lib-brand">
          <BrandMark className="lib-logo" />
          <span className="lib-kicker">{NEUTRAL_KICKER}</span>
        </div>
        <h1 className="lib-title">Course Library</h1>
        <p className="lib-sub">
          Pick a hyper-real, browser-based simulation. Each one drops you into a
          realistic scene and teaches by letting you make the call.
        </p>
      </header>

      <div className="lib-grid">
        {courses.map((c) => (
          <CourseCard key={c.id} course={c} />
        ))}
      </div>

      <footer className="lib-foot">
        {courses.length} course{courses.length === 1 ? "" : "s"} available
      </footer>
    </div>
  );
}

function CourseCard({ course }: { course: ScenarioCard & { href?: string } }) {
  const a = ACCENTS[course.id] ?? FALLBACK;
  const href = course.href ?? `?scenario=${encodeURIComponent(course.id)}`;
  return (
    <a className="lib-card" href={href}>
      <div
        className="lib-card-art"
        style={{ background: `linear-gradient(135deg, ${a.from}, ${a.to})` }}
      >
        <span className="lib-card-emoji" aria-hidden="true">
          {a.emoji}
        </span>
        <span className="lib-card-tag">{a.tag}</span>
      </div>
      <div className="lib-card-body">
        <h2 className="lib-card-title">{course.title}</h2>
        <p className="lib-card-topic">{course.topic}</p>
        <span className="lib-card-cta">
          Start course
          <span className="lib-card-arrow" aria-hidden="true">
            {"\u2192"}
          </span>
        </span>
      </div>
    </a>
  );
}
